import { useState } from "react";
import { toast } from "sonner";
import { Copy, Download, Eye, EyeOff, KeyRound, RefreshCw } from "lucide-react";
import { adminFieldCls } from "./AdminAuth";

// Values are generated locally with the browser's secure random generator.
// They are never sent to the server, stored, or logged.
const b64url = (bytes) => btoa(String.fromCharCode(...bytes)).replace(/\+/g, "-").replace(/\//g, "_");
const rand = (n) => crypto.getRandomValues(new Uint8Array(n));
const hex = (n) => Array.from(rand(n), (b) => b.toString(16).padStart(2, "0")).join("");

function tempPassword() {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789";
  for (;;) {
    const pw = "Hmf-" + Array.from(rand(18), (b) => chars[b % chars.length]).join("");
    if (/\d/.test(pw) && /[A-Za-z]/.test(pw)) return pw;
  }
}

const generate = () => ({
  FIELD_ENCRYPTION_KEY: b64url(rand(32)),
  JWT_SECRET: hex(32),
  ADMIN_BOOTSTRAP_PASSWORD: tempPassword(),
});

function SecretRow({ name, value, note, secret = false }) {
  const [show, setShow] = useState(!secret);
  const copy = () => navigator.clipboard.writeText(value).then(() => toast.success(`${name} copied`));
  return (
    <li className="py-3 border-t border-brand-line first:border-t-0" data-testid={`secret-row-${name}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <code className="text-sm font-semibold text-brand-charcoal">{name}</code>
        <div className="flex gap-2">
          {value && secret && (
            <button type="button" onClick={() => setShow((s) => !s)} aria-label={show ? `Hide ${name}` : `Show ${name}`}
              className="p-2 rounded-full hover:bg-brand-cream text-brand-muted" data-testid={`secret-toggle-${name}`}>
              {show ? <EyeOff aria-hidden="true" className="w-4 h-4" /> : <Eye aria-hidden="true" className="w-4 h-4" />}
            </button>
          )}
          <button type="button" onClick={copy} disabled={!value} data-testid={`secret-copy-${name}`}
            className="inline-flex items-center gap-1.5 text-sm text-brand-teal font-medium px-3 py-1.5 rounded-full hover:bg-brand-cream disabled:opacity-40">
            <Copy aria-hidden="true" className="w-4 h-4" /> Copy
          </button>
        </div>
      </div>
      <div className="mt-1 font-mono text-xs break-all text-brand-muted" data-testid={`secret-value-${name}`}>
        {value ? (show ? value : "•".repeat(24)) : "—"}
      </div>
      {note && <p className="text-xs text-brand-muted mt-1">{note}</p>}
    </li>
  );
}

export function DeploySecretsHelper() {
  const [vals, setVals] = useState(null);
  const [site, setSite] = useState("");
  const origin = site.trim().replace(/\/+$/, "");

  const regen = () => {
    if (vals && !window.confirm("Generate new values? The current ones will be discarded. Only do this if you have NOT saved them in production yet.")) return;
    setVals(generate());
  };

  const downloadBackup = () => {
    const text = [
      "Hosea Ministries Foundation - PRODUCTION encryption key backup",
      `Created: ${new Date().toISOString()}`,
      "Store in a password manager and/or offline. Never email or share. Losing this key = losing encrypted data.",
      "",
      `FIELD_ENCRYPTION_KEY=${vals.FIELD_ENCRYPTION_KEY}`,
      "",
    ].join("\n");
    const url = URL.createObjectURL(new Blob([text], { type: "text/plain" }));
    const a = Object.assign(document.createElement("a"), { href: url, download: "hosea-production-encryption-key.txt" });
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  return (
    <div className="space-y-5 max-w-3xl" data-testid="deploy-secrets-helper">
      <div className="bg-white border border-brand-line rounded-2xl p-5 text-sm text-brand-charcoal leading-relaxed">
        <h2 className="font-serif text-xl inline-flex items-center gap-2"><KeyRound aria-hidden="true" className="w-5 h-5 text-brand-teal" /> Production secrets helper</h2>
        <p className="mt-2 text-brand-muted">
          Values are generated in <b>this browser only</b>. They are never sent to the server, saved, or logged, and they disappear when you leave this tab.
          Copy each one into <b>Manage Publishes → Secrets</b>. Generate once, then don't regenerate after you've saved them.
        </p>
      </div>

      <div className="bg-white border border-brand-line rounded-2xl p-5">
        <label htmlFor="live-site" className="block text-xs font-medium text-brand-muted mb-1.5">Your live site address (e.g. https://your-live-domain)</label>
        <input id="live-site" className={adminFieldCls} value={site} onChange={(e) => setSite(e.target.value)} placeholder="https://" data-testid="live-site-input" />
        <ul className="mt-3">
          <SecretRow name="ADMIN_PORTAL_URL" value={origin ? `${origin}/admin` : ""} />
          <SecretRow name="REACT_APP_SITE_URL" value={origin} />
          <SecretRow name="ADMIN_BOOTSTRAP_EMAIL" value="info@hoseaministriesfoundation.org" />
          <SecretRow name="EMAIL_DELIVERY_ENABLED" value="true" />
        </ul>
      </div>

      <div className="bg-white border border-brand-line rounded-2xl p-5">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="font-medium text-brand-charcoal">Generated secrets</div>
          <button type="button" onClick={regen} data-testid="secrets-generate"
            className="inline-flex items-center gap-2 bg-brand-teal text-white rounded-full px-4 py-2 text-sm font-medium hover:bg-brand-tealDark">
            <RefreshCw aria-hidden="true" className="w-4 h-4" /> {vals ? "Regenerate" : "Generate values"}
          </button>
        </div>
        {vals && (
          <>
            <ul className="mt-3">
              <SecretRow secret name="FIELD_ENCRYPTION_KEY" value={vals.FIELD_ENCRYPTION_KEY} note="Back this up BEFORE redeploying (button below). If it's lost, encrypted data can't be recovered." />
              <SecretRow secret name="JWT_SECRET" value={vals.JWT_SECRET} />
              <SecretRow secret name="ADMIN_BOOTSTRAP_PASSWORD" value={vals.ADMIN_BOOTSTRAP_PASSWORD} note="Your first-sign-in password. You'll be required to change it, and then you can delete this secret." />
            </ul>
            <button type="button" onClick={downloadBackup} data-testid="secrets-download-backup"
              className="mt-4 inline-flex items-center gap-2 border border-brand-teal text-brand-teal rounded-full px-4 py-2 text-sm font-medium hover:bg-brand-cream">
              <Download aria-hidden="true" className="w-4 h-4" /> Download encryption key backup file
            </button>
            <p className="text-xs text-brand-muted mt-2">Move the file into your password manager or onto an offline drive, then delete it from Downloads.</p>
          </>
        )}
      </div>
    </div>
  );
}
